import React, { useState } from 'react';
import { Form, Input, Select, message, Button } from 'antd';
import EditableDrawer from 'components/EditableDrawer';
import ZipcodeInput from 'components/ZipcodeInput';
import { usePickupRouteZipcodeConflictCheck } from '../hooks/usePickupRouteZipcodeConflictCheck';
import { useTranslation } from 'react-i18next';

interface RouteData {
  route: string;
  zipcodes: string;
  driverId: string;
  vehicleId: string;
  vehicleTypeName: string;
}

interface CreateRouteDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: RouteData) => void;
  loading?: boolean;
  driverOptions: { label: string; value: string }[];
  vehicleOptions: { label: string; value: string }[];
  warehouseId?: number;
  // 添加完整的车辆数据
  vehicles: Array<{
    id: number;
    vehicleNumber: string;
    licensePlate: string;
    status: number;
    vehicleModelId: number;
    vehicleModelName: string;
    vehicleTypeName?: string;
  }>;
}

const CreateRouteDrawer: React.FC<CreateRouteDrawerProps> = ({
  visible,
  onClose,
  onSubmit,
  loading = false,
  driverOptions,
  vehicleOptions,
  warehouseId,
  vehicles,
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [zipcodesData, setZipcodesData] = useState<string[]>([]);
  const {t} = useTranslation();
  // 使用zipcode冲突检查hook
  const { checkZipcodeConflict, loading: conflictCheckLoading } = usePickupRouteZipcodeConflictCheck();

  // 处理Vehicle选择变化
  const handleVehicleChange = (vehicleId: string) => {
    if (!vehicleId) {
      form.setFieldValue('vehicleTypeName', undefined);
      return;
    }

    // 查找选中的车辆信息
    const selectedVehicle = vehicles.find(v => v.id.toString() === vehicleId);
    if (selectedVehicle) {
      // 根据车辆ID查找对应的车型名称
      const vehicleTypeName = selectedVehicle.vehicleTypeName;
      form.setFieldValue('vehicleTypeName', vehicleTypeName);
    }
  };

  // 打开抽屉时重置表单
  React.useEffect(() => {
    if (visible) {
      form.resetFields();
      setZipcodesData([]);
      form.setFieldsValue({ zipcodes: '' });
    }
  }, [visible, form]);

  // 提交表单
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      
      // 将zipcodes转换为JSON格式
      if (values.zipcodes) {
        const zipcodesArray = values.zipcodes
          .split(/[,\s\n\r\t;]+/)
          .map((zip: string) => zip.trim())
          .filter((zip: string) => zip.length > 0);
        
        if (zipcodesArray.length > 0) {
          // 检查zipcode冲突（包含warehouse pickup zone验证）
          const conflictResult = await checkZipcodeConflicts(zipcodesArray, values.route);
          if (conflictResult.hasConflict) {
            // 首先检查路线名称冲突
            if (conflictResult.hasNameConflict && conflictResult.conflictingRouteNames.length > 0) {    
              message.error(t('pickup.pickupTask.pageDetails.messages.routeNameAlreadyExists', { routeName: values.route }) || '');
              return;
            }
            
            // 检查warehouse pickup zone验证冲突
            if (conflictResult.hasWarehouseValidationConflict && conflictResult.invalidWarehouseZipcodes.length > 0) {
              const invalidZipcodes = conflictResult.invalidWarehouseZipcodes.join(', ');
              message.error(t('pickup.pickupTask.pageDetails.messages.invalidWarehouseZipcodes', { invalidZipcodes: invalidZipcodes }) || '');
              return;
            }
            
            // 然后检查邮编冲突
            if (conflictResult.conflictingZipcodes.length > 0) {
              let zipcodeErrorMessage = t('pickup.pickupTask.pageDetails.messages.conflictingZipcodes', { conflictingZipcodes: conflictResult.conflictingZipcodes.join(', ') }) || '';
              
              if (conflictResult.conflictDetails && conflictResult.conflictDetails.length > 0) {
                const conflictInfo = conflictResult.conflictDetails.map(detail => 
                  t('pickup.pickupTask.pageDetails.messages.conflictInfo', { conflictingZipcode: detail.conflictingZipcode, routeName: detail.routeName }) || ''
                ).join(', ');
                zipcodeErrorMessage += `\n(${conflictInfo})`;
              }
              
              message.error(zipcodeErrorMessage);
              return;
            }
            
            return;
          }
        }
        
        values.zipcodes = JSON.stringify(zipcodesArray);
      } else {
        values.zipcodes = JSON.stringify([]);
      }
      
      await onSubmit(values);
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 检查zipcode冲突的函数
  const checkZipcodeConflicts = async (zipcodes: string[], routeName: string) => {
    if (!warehouseId || zipcodes.length === 0) {
      return {
        hasConflict: false,
        conflictingRoutes: [],
        conflictingZipcodes: [],
        conflictDetails: [],
        hasNameConflict: false,
        conflictingRouteNames: [],
        hasWarehouseValidationConflict: false,
        invalidWarehouseZipcodes: []
      };
    }

    try {
      const conflictResult = await checkZipcodeConflict(
        warehouseId,
        zipcodes,
        undefined, // 创建模式没有excludeRouteId
        routeName
      );

      if (conflictResult.error) {
        console.error('Error checking zipcode conflicts:', conflictResult.error);
        message.error(t('pickup.pickupTask.pageDetails.messages.errorCheckingZipcodeConflicts') || '');
        return {
          hasConflict: false,
          conflictingRoutes: [],
          conflictingZipcodes: [],
          conflictDetails: [],
          hasNameConflict: false,
          conflictingRouteNames: [],
          hasWarehouseValidationConflict: false,
          invalidWarehouseZipcodes: []
        };
      }

      return conflictResult;
    } catch (error) {
      console.error('Error in checkZipcodeConflicts:', error);
      message.error(t('pickup.pickupTask.pageDetails.messages.errorCheckingZipcodeConflicts') || '');
      return {
        hasConflict: false,
        conflictingRoutes: [],
        conflictingZipcodes: [],
        conflictDetails: [],
        hasNameConflict: false,
        conflictingRouteNames: [],
        hasWarehouseValidationConflict: false,
        invalidWarehouseZipcodes: []
      };
    }
  };

  return (
    <EditableDrawer
      title={t('pickup.pickupTask.pageDetails.titles.createNewRoute') || ''}
      visible={visible}
      onClose={onClose}
      onSubmit={handleSubmit}
      confirmText={isSubmitting ? t('components.button.creating') || '' : t('components.button.create') || ''}
      cancelText={t('components.button.cancel') || ''}
      confirmDisabled={isSubmitting || conflictCheckLoading}
      width={550}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <Form.Item 
          name="route" 
          label={t('pickup.pickupTask.pageDetails.fields.routeName') || ''}
          rules={[
            { required: true, message: t('pickup.pickupTask.pageDetails.messages.pleaseInputRouteName') || '' },
            { min: 2, message: t('pickup.pickupTask.pageDetails.messages.routeNameMustBeAtLeast2Characters') || '' },
            { max: 50, message: t('pickup.pickupTask.pageDetails.messages.routeNameMustNotExceed50Characters') || '' }
          ]}
        >
          <Input 
            placeholder={t('pickup.pickupTask.pageDetails.placeholders.enterRouteName') || ''}
          />
        </Form.Item>

        <Form.Item
          name="zipcodes"
          label={t('pickup.pickupTask.pageDetails.fields.zipcodes') || ''}
          rules={[
            { required: true, message: t('pickup.pickupTask.pageDetails.messages.pleaseInputZipcodes') || '' }
          ]}
        >
          <ZipcodeInput
            data={{ zipcodes: zipcodesData }}
            isEditing={true}
            onChange={(data) => {
              setZipcodesData(data.zipcodes);
              const zipcodesString = data.zipcodes.join(', ');
              form.setFieldValue('zipcodes', zipcodesString);
            }}
            placeholder={t('pickup.pickupTask.pageDetails.placeholders.enterZipcodes') || ''}
            rows={4}
          />
        </Form.Item>

        <Form.Item 
          name="driverId" 
          label={t('pickup.pickupTask.pageDetails.fields.driver') || ''}
          rules={[{ required: true, message: t('pickup.pickupTask.pageDetails.messages.pleaseSelectDriver') || '' }]}
        >
          <Select 
            options={driverOptions}
            placeholder={t('pickup.pickupTask.pageDetails.placeholders.selectDriver') || ''}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            allowClear
          />
        </Form.Item>

        <Form.Item 
          name="vehicleId" 
          label={t('pickup.pickupTask.pageDetails.fields.vehicle') || ''}
          rules={[{ required: true, message: t('pickup.pickupTask.pageDetails.messages.pleaseSelectVehicle') || '' }]}
        >
          <Select
            options={vehicleOptions}
            placeholder={t('pickup.pickupTask.pageDetails.placeholders.selectVehicle') || ''}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={t('pickup.pickupTask.pageDetails.messages.noVehiclesFound') || ''}
            allowClear
            onChange={handleVehicleChange}
          />
        </Form.Item>

        <Form.Item
          name="vehicleTypeName"
          label={t('pickup.pickupTask.pageDetails.fields.vehicleModel') || ''}
        >
          <Input 
            placeholder={t('pickup.pickupTask.pageDetails.placeholders.autoAssignedBasedOnVehicleSelection') || ''}
            disabled
          />
        </Form.Item>
      </Form>
    </EditableDrawer>
  );
};

export default CreateRouteDrawer;
